import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker } from 'antd';
import moment from 'moment';
import { generateInvestmentReviewReport } from './GeneralService';
import InvestmentReviewExcelExport from "../../Utils/InvestmentReviewExcelExport";
import { errHandler } from '../../helper/helper';

const { Option } = Select;
const { RangePicker } = DatePicker;

const InvestmentReviewReport = ({
                                    loginPlaceId,
                                    districtData,
                                    districtName,
                                    divisionalId,
                                    branchId,
                                    loading,
                                    setLoading
                                }) => {
    const [selections, setSelections] = useState({
        districts: [],
        divisionalAreas: [],
        branches: branchId ? [Number(branchId)] : [],
        startDate: null,
        endDate: null
    });

    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);

    useEffect(() => {
        if (loginPlaceId === "4" && districtName && districtData?.length) {
            const userDistrict = districtData.find(d => d.districtName === districtName);
            if (userDistrict) {
                setSelections(prev => ({ ...prev, districts: [userDistrict.districtId] }));
            }
        } else if (loginPlaceId === "2" && divisionalId) {
            setSelections(prev => ({ ...prev, divisionalAreas: [Number(divisionalId)] }));
        } else if (loginPlaceId !== "1" && branchId) {
            setSelections(prev => ({ ...prev, branches: [Number(branchId)] }));
        }
    }, [loginPlaceId, branchId, divisionalId, districtName, districtData]);

    useEffect(() => {
        let filtered = districtData?.flatMap(d => d.divisionalAreaDtoList || []) || [];
        if (selections.districts.length > 0) {
            filtered = districtData
                .filter(d => selections.districts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);
        }
        setAvailableDivisionalAreas(filtered);
    }, [selections.districts, districtData]);

    useEffect(() => {
        let filtered = [];
        if (selections.divisionalAreas.length > 0) {
            filtered = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(da => selections.divisionalAreas.includes(da.divAId))
                .flatMap(da => da.branchDtoList || []);
        } else if (selections.districts.length > 0) {
            filtered = districtData
                .filter(d => selections.districts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(da => da.branchDtoList || []);
        } else {
            filtered = districtData?.flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(da => da.branchDtoList || []) || [];
        }
        setAvailableBranches(filtered);
    }, [selections.divisionalAreas, selections.districts, districtData]);

    const disabledDate = current => current && current > moment().endOf('day');

    const handleDistrictChange = (values) => {
        let cleaned = values.filter(v => v !== 'ALL').map(Number);
        if (values.includes('ALL')) {
            cleaned = districtData.map(d => d.districtId);
        }
        setSelections(prev => ({ ...prev, districts: cleaned, divisionalAreas: [], branches: [] }));
    };

    const handleDivAreaChange = (values) => {
        let cleaned = values.filter(v => v !== 'ALL').map(Number);
        if (values.includes('ALL')) {
            cleaned = availableDivisionalAreas.map(d => d.divAId);
        }
        setSelections(prev => ({ ...prev, divisionalAreas: cleaned, branches: [] }));
    };

    const handleBranchChange = (values) => {
        let cleaned = values.filter(v => v !== 'ALL').map(Number);
        if (values.includes('ALL')) {
            cleaned = availableBranches.map(b => b.id);
        }
        setSelections(prev => ({ ...prev, branches: cleaned }));
    };

    const handleDateChange = (dates) => {
        setSelections(prev => ({
            ...prev,
            startDate: dates?.[0] || null,
            endDate: dates?.[1] || null
        }));
    };

    const generateReport = async () => {
        if (!selections.startDate || !selections.endDate) {
            alert("Please select a date range");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                selectedDate: selections.startDate.format("YYYY-MM-DD"),
                endDate: selections.endDate.format("YYYY-MM-DD"),
                branchIds: selections.branches.length > 0 ? selections.branches : null
            };

            const response = await generateInvestmentReviewReport(payload);
            const rawData = response?.data || response || [];

            if (!Array.isArray(rawData) || rawData.length === 0) {
                alert("No data found.");
                setLoading(false);
                return;
            }

            const processedData = processDataForExcel(rawData);

            const branchNames = [...new Set(processedData.map(item => item.branchName).filter(Boolean))];

            const getDivisionalName = () => {
                if (selections.divisionalAreas.length === 0) return "All Divisional Areas";
                const selectedAreas = availableDivisionalAreas
                    .filter(area => selections.divisionalAreas.includes(area.divAId))
                    .map(area => area.divAIdName);
                return selectedAreas.length > 0 ? selectedAreas.join(", ") : "All Divisional Areas";
            };

            const getDistrictName = () => {
                if (selections.districts.length === 0) return "All Districts";
                const selectedDistricts = districtData
                    ?.filter(d => selections.districts.includes(d.districtId))
                    .map(d => d.districtName);
                return selectedDistricts?.length > 0 ? selectedDistricts.join(", ") : "All Districts";
            };

            const meta = {
                startDate: selections.startDate.format("DD/MM/YYYY"),
                endDate: selections.endDate.format("DD/MM/YYYY"),
                districtName: getDistrictName(),
                divisionalName: getDivisionalName(),
                branchName: branchNames.length ? branchNames.join(", ") : "All Branches"
            };

            await InvestmentReviewExcelExport({
                data: processedData,
                meta: meta
            });

        } catch (err) {
            errHandler(err);
            alert("Error generating report.");
        } finally {
            setLoading(false);
        }
    };

    const processDataForExcel = (rawData) => {
        const knownBanks = [
            { bankId: 1, bankName: "Bank Of Ceylon" },
            { bankId: 2, bankName: "NSB" },
            { bankId: 3, bankName: "Seylan Bank" },
            { bankId: 4, bankName: "Sampath Bank" },
            { bankId: 5, bankName: "Commercial Bank" },
            { bankId: 6, bankName: "HNB" },
            { bankId: 7, bankName: "Peoples Bank" },
            { bankId: 8, bankName: "HSBC" },
            { bankId: 9, bankName: "Regional Development Bank (RDB)" },
            { bankId: 10, bankName: "HDFC" },
            { bankId: 11, bankName: "SMIB" }
        ];

        const cleanedData = rawData.map(item => ({
            branchName: item.branchName || '',
            bankName: item.bankName || '',
            bankId: item.bankId || 0,
            tbill: Number(item.tbill || 0),
            fixed: Number(item.fixed || 0),
            other: Number(item.other || 0),
            total: Number(item.total || (Number(item.tbill || 0) + Number(item.fixed || 0) + Number(item.other || 0)))
        }));

        const branches = [...new Set(cleanedData.map(item => item.branchName).filter(Boolean))];

        const finalData = [];

        branches.forEach(branch => {
            knownBanks.forEach(knownBank => {
                const branchData = cleanedData.find(item =>
                    item.branchName === branch && item.bankName === knownBank.bankName
                );

                finalData.push({
                    branchName: branch,
                    bankName: knownBank.bankName,
                    bankId: knownBank.bankId,
                    tbill: branchData?.tbill || 0,
                    fixed: branchData?.fixed || 0,
                    other: branchData?.other || 0,
                    total: branchData?.total || 0
                });
            });
        });

        if (branches.length === 0) {
            knownBanks.forEach(knownBank => {
                const bankData = cleanedData.filter(item => item.bankName === knownBank.bankName);

                const totalTbill = bankData.reduce((sum, item) => sum + (item.tbill || 0), 0);
                const totalFixed = bankData.reduce((sum, item) => sum + (item.fixed || 0), 0);
                const totalOther = bankData.reduce((sum, item) => sum + (item.other || 0), 0);
                const total = bankData.reduce((sum, item) => sum + (item.total || 0), 0);

                finalData.push({
                    branchName: "All Branches",
                    bankName: knownBank.bankName,
                    bankId: knownBank.bankId,
                    tbill: totalTbill,
                    fixed: totalFixed,
                    other: totalOther,
                    total: total
                });
            });
        }

        return finalData;
    };

    const isGenerateDisabled = loading || !selections.startDate || !selections.endDate;

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">

                    {(loginPlaceId === "3" || loginPlaceId === "4") && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selections.districts}
                                        onChange={handleDistrictChange}
                                        disabled={loginPlaceId === "4"}
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        allowClear
                                    >
                                        {loginPlaceId === "4" ? (
                                            districtData
                                                .filter(d => d.districtName === districtName)
                                                .map(d => (
                                                    <Option key={d.districtId} value={d.districtId}>
                                                        {d.districtName}
                                                    </Option>
                                                ))
                                        ) : (
                                            <>
                                                <Option key="ALL" value="ALL">Select All Districts</Option>
                                                {districtData?.map(d => (
                                                    <Option key={d.districtId} value={d.districtId}>
                                                        {d.districtName}
                                                    </Option>
                                                ))}
                                            </>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Divisional Areas">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select divisional areas"
                                        style={{ width: '100%' }}
                                        value={selections.divisionalAreas}
                                        onChange={handleDivAreaChange}
                                        disabled={availableDivisionalAreas.length === 0 || loginPlaceId === "2"}
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        allowClear
                                    >
                                        <Option key="ALL" value="ALL">Select All Divisional Areas</Option>
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>
                                                {d.divAIdName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Branches">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={selections.branches}
                                    onChange={handleBranchChange}
                                    disabled={availableBranches.length === 0 || loginPlaceId === "1"}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    allowClear
                                >
                                    <Option key="ALL" value="ALL">Select All Branches</Option>
                                    {availableBranches.map(b => (
                                        <Option key={b.id} value={b.id}>
                                            {b.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Investment Date Range">
                                <RangePicker
                                    style={{ width: '100%' }}
                                    value={[selections.startDate, selections.endDate]}
                                    onChange={handleDateChange}
                                    format="YYYY-MM-DD"
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={generateReport}
                                    disabled={isGenerateDisabled}
                                    className="generate-btn"
                                    style={{ width: '100%' }}
                                    type="primary"
                                    size="large"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>

                </Form>
            </Card>
        </div>
    );
};

export default InvestmentReviewReport;